import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Skeleton, Stack, TextField, Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

const ACCOUNT_COLORS = {
  mylove4bollywood: "#E91E63",
  filmymafia: "#FF5200",
  "log.kya.kahenge": "#9C27B0",
  sarcasmicguy: "#2196F3",
  idiotic_sperm: "#4CAF50",
  webseriesmania: "#FF9800",
};

const FADE_SLIDE = {
  "@keyframes fadeSlideIn": {
    "0%": { opacity: 0, transform: "translateY(8px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
};

function getAccountColor(username) {
  return ACCOUNT_COLORS[username] || "#FF5200";
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// IST calendar-day key, independent of the viewer's device timezone.
function istDateKey(d) {
  const ist = new Date(d.getTime() + IST_OFFSET_MS);
  return `${ist.getUTCFullYear()}-${ist.getUTCMonth()}-${ist.getUTCDate()}`;
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  if (istDateKey(d) === istDateKey(now)) return "Today";
  if (istDateKey(d) === istDateKey(new Date(now.getTime() - 86400000))) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
}

function groupMessagesByDate(messages) {
  const groups = {};
  for (const msg of messages) {
    const label = formatDateLabel(msg.sentAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
  }
  return groups;
}

function DateSeparator({ label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", my: 2, gap: 1.5 }}>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          fontFamily: "Quicksand",
          color: "#9CA3AF",
          px: 1.5,
          py: 0.4,
          bgcolor: "rgba(0,0,0,0.04)",
          borderRadius: 99,
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
    </Box>
  );
}

function MessageBubble({ message, accentColor }) {
  const isUs = message.isFromUs;
  const isSending = message._status === "sending";
  const isFailed = message._status === "failed";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUs ? "flex-end" : "flex-start",
        mb: 0.75,
        opacity: isSending ? 0.6 : 1,
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.2s ease both",
      }}
    >
      <Box
        sx={{

          maxWidth: {
            xs:'85%',
            sm:'75%',
            md:'68%'
          },
          background: isFailed
            ? "rgba(229,57,53,0.12)"
            : isUs
            ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}CC 100%)`
            : "rgba(255,255,255,0.97)",
          color: isFailed ? "#E53935" : isUs ? "#fff" : "#1C1C2E",
          borderRadius: isUs ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          px: 2,
          py: 1.25,
          boxShadow: isUs
            ? `0 4px 16px ${accentColor}40`
            : "0 2px 8px rgba(0,0,0,0.07)",
          border: isFailed ? "1px solid rgba(229,57,53,0.3)" : isUs ? "none" : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {!isUs && message.fromUsername && (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "Quicksand",
              color: "#9CA3AF",
              mb: 0.25,
              letterSpacing: 0.3,
            }}
          >
            @{message.fromUsername}
          </Typography>
        )}
        {message.text ? (
          <Typography
            sx={{
              fontSize: 13.5,
              fontFamily: "Quicksand",
              fontWeight: 600,
              lineHeight: 1.55,
              wordBreak: "break-word",
            }}
          >
            {message.text}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: "Quicksand",
              fontStyle: "italic",
              opacity: 0.55,
            }}
          >
            [attachment]
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: 10,
            fontFamily: "Quicksand",
            color: isUs ? "rgba(255,255,255,0.65)" : "#9CA3AF",
            textAlign: "right",
            mt: 0.5,
          }}
        >
          {formatTime(message.sentAt)}
        </Typography>
      </Box>
    </Box>
  );
}

function Composer({ conversation, pending, onSend, onRetry, onDismiss }) {
  const [text, setText] = useState("");
  const accentColor = getAccountColor(conversation?.ourAccount);
  const noToken = !conversation?.hasActiveToken;
  const windowClosed = conversation?.messagingWindow === "closed";
  // Human agent (24h-7d) replies require a Meta App Review approval we don't have yet,
  // so treat that window as disabled too, same as closed, until that's approved.
  const isHumanAgentWindow = conversation?.messagingWindow === "human_agent";
  const disabled = noToken || windowClosed || isHumanAgentWindow;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    setText("");
    onSend(trimmed);
  };

  return (
    <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(255,255,255,0.85)", flexShrink: 0 }}>
      {pending?.filter((p) => p.status === "failed").map((p) => (
        <Box key={p.tempId} sx={{ px: 2.5, pt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 11, fontFamily: "Quicksand", color: "#E53935" }}>
            Failed to send "{p.text.slice(0, 30)}{p.text.length > 30 ? "…" : ""}"
          </Typography>
          <Typography
            onClick={() => onRetry(p)}
            sx={{ fontSize: 11, fontFamily: "Quicksand", fontWeight: 800, color: accentColor, cursor: "pointer" }}
          >
            Retry
          </Typography>
          <Typography
            onClick={() => onDismiss(p.tempId)}
            sx={{ fontSize: 11, fontFamily: "Quicksand", fontWeight: 700, color: "#9CA3AF", cursor: "pointer" }}
          >
            Dismiss
          </Typography>
        </Box>
      ))}
      {disabled ? (
        <Typography sx={{ px: 2.5, py: 1.5, fontSize: 12, fontFamily: "Quicksand", color: "#9CA3AF", fontStyle: "italic" }}>
          {noToken
            ? `Sending is unavailable — no active Instagram token for @${conversation?.ourAccount}.`
            : "This creator hasn't messaged in the last 24 hours — Instagram doesn't allow replying until they message again."}
        </Typography>
      ) : (
        // Human agent (24h-7d) notice — re-enable once the Meta App Review for this feature is approved.
        // conversation?.messagingWindow === "human_agent" && (
        //   <Typography sx={{ px: 2.5, pt: 1, fontSize: 11, fontFamily: "Quicksand", color: "#9CA3AF" }}>
        //     It's been over 24h since their last message — this will send as a human agent reply.
        //   </Typography>
        // )
        null
      )}
      {!disabled && (
        <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                fontFamily: "Quicksand",
                fontSize: 13.5,
                background: "rgba(0,0,0,0.02)",
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!text.trim()}
            sx={{
              bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)",
              color: text.trim() ? "#fff" : "#9CA3AF",
              "&:hover": { bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)", opacity: 0.9 },
              width: 38,
              height: 38,
            }}
          >
            <SendRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}

function ChatPanel({ conversation, isLoading, pending, onSend, onRetry, onDismiss }) {
  const scrollRef = useRef(null);
  const accentColor = getAccountColor(conversation?.ourAccount);
  const messages = conversation?.messages || [];
  const pendingForThisConv = (pending || []).filter((p) => p.conversationId === conversation?.conversationId);
  const combined = [
    ...messages,
    ...pendingForThisConv.map((p) => ({
      igMessageId: p.tempId,
      fromUsername: null,
      text: p.text,
      sentAt: p.sentAt,
      isFromUs: true,
      _status: p.status,
    })),
  ];
  const grouped = groupMessagesByDate(combined);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, pending]);

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, p: 2.5 }}>
        {[...Array(6)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
            <Skeleton variant="rounded" width={`${40 + (i % 3) * 15}%`} height={44} sx={{ borderRadius: i % 2 === 0 ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Chat header */}
      <Box
        sx={{
          px: {
            xs:1.5,
            sm:2.5
          },
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(8px)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: accentColor,
            boxShadow: `0 0 0 3px ${accentColor}30`,
          }}
        />
        <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 14, color: "#1C1C2E" }}>
          @{conversation?.ourAccount}
        </Typography>
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 12, color: "#9CA3AF", ml: "auto" }}>
          {messages.length} {messages.length === 1 ? "message" : "messages"}
        </Typography>
      </Box>

      {/* Messages scroll area */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          background: "linear-gradient(145deg, rgba(237,241,255,0.4) 0%, rgba(242,238,255,0.3) 50%, rgba(255,240,237,0.3) 100%)",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.12)", borderRadius: 2 },
        }}
      >
        {Object.entries(grouped).map(([dateLabel, msgs]) => (
          <Box key={dateLabel}>
            <DateSeparator label={dateLabel} />
            {msgs.map((msg, i) => (
              <MessageBubble key={msg.igMessageId || `msg-${dateLabel}-${i}`} message={msg} accentColor={accentColor} />
            ))}
          </Box>
        ))}
      </Box>

      <Composer
        conversation={conversation}
        pending={pendingForThisConv}
        onSend={(text) => onSend(conversation.conversationId, text)}
        onRetry={onRetry}
        onDismiss={onDismiss}
      />
    </Box>
  );
}

function ConversationSidebar({ conversations, selectedIdx, onSelect }) {
  return (
    <Box
      sx={{
        width: {
         xs: '100%',
         sm: 180,
         md: 220
        },
        flexShrink: 0,
        borderRight: "1px solid rgba(0,0,0,0.06)",
        overflowY: "auto",
        background: "rgba(255,255,255,0.6)",
        "&::-webkit-scrollbar": { width: 3 },
      }}
    >
      <Typography
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          fontFamily: "Quicksand",
          fontSize: 10,
          fontWeight: 800,
          color: "#9CA3AF",
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        Accounts
      </Typography>
      {conversations.map((conv, idx) => {
        const color = getAccountColor(conv.ourAccount);
        const isSelected = idx === selectedIdx;
        return (
          <Box
            key={conv.conversationId || idx}
            onClick={() => onSelect(idx)}
            sx={{
              px: 2,
              py: 1.5,
              cursor: "pointer",
              borderLeft: isSelected ? `3px solid ${color}` : "3px solid transparent",
              bgcolor: isSelected ? `${color}0D` : "transparent",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: `${color}0D` },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontFamily: "Quicksand",
                  fontWeight: isSelected ? 800 : 600,
                  fontSize: 12.5,
                  color: isSelected ? color : "#374151",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                @{conv.ourAccount}
              </Typography>
            </Stack>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 10, color: "#9CA3AF", pl: 2.25, mt: 0.25 }}>
              {formatRelativeTime(conv.lastMessageAt)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function EmptyState() {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        py: 8,
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.5s ease both",
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(255,82,0,0.08) 0%, rgba(255,140,0,0.05) 100%)",
          border: "2px dashed rgba(255,82,0,0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 28, color: "#FF5200", opacity: 0.55 }} />
      </Box>
      <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, color: "#1C1C2E", fontSize: 15 }}>
        No conversations yet
      </Typography>
      <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280", fontSize: 13, maxWidth: 300, textAlign: "center" }}>
        Once our accounts message this creator on Instagram, the conversation will appear here.
      </Typography>
    </Box>
  );
}

let pendingIdCounter = 0;

function CreatorConversationsTab({ conversations, isLoading, onSendMessage }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [pending, setPending] = useState([]);
  const hasMultiple = conversations && conversations.length > 1;
  const selected = conversations?.[selectedIdx] || null;

  const handleSend = async (conversationId, text) => {
    const tempId = `pending-${++pendingIdCounter}`;
    setPending((prev) => [...prev, { tempId, conversationId, text, sentAt: new Date().toISOString(), status: "sending" }]);
    try {
      // Parent writes the real message into the shared conversations cache before this
      // resolves, so removing the placeholder here doesn't leave a gap in the message list.
      await onSendMessage(conversationId, text);
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
    } catch (err) {
      setPending((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, status: "failed" } : p)));
    }
  };

  const handleRetry = (item) => {
    setPending((prev) => prev.filter((p) => p.tempId !== item.tempId));
    handleSend(item.conversationId, item.text);
  };

  const handleDismiss = (tempId) => {
    setPending((prev) => prev.filter((p) => p.tempId !== tempId));
  };

  if (!isLoading && (!conversations || conversations.length === 0)) {
    return <EmptyState />;
  }

  return (
    <Box
      sx={{
        mt: 3,
        display: "flex",
        flexDirection:{
          xs:'column',
          md:'row'
        },

        border: "1px solid rgba(0,0,0,0.07)",
        borderRadius: 3,
        overflow: "hidden",
        height: {
          xs:'auto',
          md:520
        },
        minHeight:{
          xs:500,
          md:520
        },
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.35s ease both",
      }}
    >
      {hasMultiple && (
        <ConversationSidebar
          conversations={conversations}
          selectedIdx={selectedIdx}
          onSelect={setSelectedIdx}
        />
      )}
      <ChatPanel
        conversation={selected}
        isLoading={isLoading}
        pending={pending}
        onSend={handleSend}
        onRetry={handleRetry}
        onDismiss={handleDismiss}
      />
    </Box>
  );
}

export default CreatorConversationsTab;
